import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { useState } from 'react'
import { ArrowLeft, Lock, Paperclip, Play, MessageSquare, Edit3, EyeOff, Eye } from 'lucide-react'
import {
  artifacts,
  matters as mattersApi,
  results as resultsApi,
  runPipeline,
  type ArtifactDetail,
} from '../api'
import { useAuth } from '../auth'
import BodyPreview from '../components/BodyPreview'
import ClassificationCard from '../components/ClassificationCard'
import EditableText from '../components/EditableText'
import { copyArtifactToClipboard } from '../components/artifactCopy'

export default function ArtifactViewer() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && (auth.user.is_staff || auth.user.is_superuser)
  const artId = id ? Number(id) : null
  // Whether we were entered via the Studio route. Matters for: where the
  // back button lands (Studio Artifacts vs Workspace) and which top nav
  // is rendered (the parent <Layout>/<StudioLayout> already chose this
  // based on the route — we only need to make our own back link match).
  const fromStudio = window.location.pathname.startsWith('/studio/')
  // Capture the URL the user came from on first mount, so back goes to
  // wherever they actually were — Studio Artifacts (with filters/search
  // intact) vs anywhere else. Empty string when navigated to directly
  // (cold open / new tab). Frozen via useState's initializer so it
  // doesn't update on in-page nav (e.g. opening a child attachment).
  //
  // Source priority:
  //   1. sessionStorage (set by App's LocationTracker on every SPA
  //      navigation) — this is authoritative for in-app moves because
  //      document.referrer is only updated on full page loads, not on
  //      react-router transitions.
  //   2. document.referrer — fallback for fresh tab loads where the
  //      sessionStorage entry hasn't been written yet.
  const [referrer] = useState<string>(() => {
    try {
      const hasStudioAccess = auth.status === 'authenticated' && auth.user.has_studio_access
      const stored = sessionStorage.getItem('me_last_browser_location') || ''
      if (stored
          && !stored.startsWith('/artifact/')
          && !stored.startsWith('/studio/artifact/')) {
        // Defense in depth: if the stored path is under /studio/ but the
        // current user doesn't have studio access (e.g. a previous admin
        // session left a breadcrumb that the current non-admin user
        // shouldn't be sent back to), fall through to the workspace
        // fallback. RequireAdmin would lock-screen them, but cleaner to
        // not navigate there in the first place.
        if (!stored.startsWith('/studio/') || hasStudioAccess) {
          return stored
        }
      }
      const r = document.referrer
      if (!r) return ''
      const u = new URL(r)
      if (u.origin !== window.location.origin) return ''
      if (u.pathname.startsWith('/artifact/') || u.pathname.startsWith('/studio/artifact/')) return ''
      // Same studio-access check on the document.referrer fallback.
      if (u.pathname.startsWith('/studio/') && !hasStudioAccess) return ''
      return u.pathname + u.search
    } catch { return '' }
  })

  const detail = useQuery({
    queryKey: ['artifact', artId],
    queryFn: () => (artId ? artifacts.detail(artId) : null),
    enabled: !!artId,
  })
  const results = useQuery({
    queryKey: ['results', 'artifact', artId],
    queryFn: () => (artId ? resultsApi.list({ artifact: artId }) : Promise.resolve([])),
    enabled: !!artId,
  })
  // Params are part of the queryKey so divergent fetches can't
  // corrupt each other's cache. See Workspace's matter query for the
  // background — same key, different params used to mean the two
  // pages would step on each other.
  const matters = useQuery({
    queryKey: ['matters', { limit: 500 }],
    queryFn: () => mattersApi.list({ limit: 500 }),
  })

  const run = useMutation({
    mutationFn: () => runPipeline(artId!, { force: true }),
  })
  const [copied, setCopied] = useState(false)
  const toggleIgnore = useMutation({
    mutationFn: () => (detail.data?.ignored
      ? artifacts.unignore([artId!])
      : artifacts.ignore([artId!], 'manual')),
    onSuccess: (_data, _vars, _ctx) => {
      // Refetch this detail + invalidate the browser list cache so going
      // back picks up the new ignored state.
      qc.invalidateQueries({ queryKey: ['artifact', artId] })
      qc.invalidateQueries({ queryKey: ['artifactsPage'] })
    },
  })

  if (detail.isLoading) {
    return <div className="me-scroll p-8 text-[13px] me-text-mute">Loading…</div>
  }
  if (!detail.data) {
    return (
      <div className="me-scroll p-8">
        <div className="text-[13px] me-text-mute">Artifact not found.</div>
        <button
          onClick={() => navigate(referrer || (fromStudio ? '/studio/artifacts' : '/workspace'))}
          className="me-btn me-btn-outline mt-3"
        >
          <ArrowLeft size={12} /> Back
        </button>
      </div>
    )
  }

  const a = detail.data
  const matterById = new Map((matters.data ?? []).map((m) => [m.id, m]))

  return (
    <div className="me-scroll grid h-full grid-cols-[1fr_320px] gap-px overflow-hidden bg-[color:var(--me-border)]">
      <article className="me-scroll overflow-auto bg-[color:var(--me-surface)] p-8">
        {/* Header */}
        <div className="mb-5 flex items-center gap-2">
          {/* Back button — always navigates to the workspace scoped to
              this artifact's matter (with the artifact selected), so
              the destination is predictable regardless of how the user
              got here (in-app link, cmd-click new tab, direct URL,
              page reload, etc.). Earlier we tried navigate(-1) when
              window.history.length > 1, but that's unreliable: a
              hard-reload resets history to 1 (good), but ANY in-app
              nav within the viewer (toggling ignored, opening a child
              attachment, etc.) bumps it to 2+ and navigate(-1) lands
              on the artifact's PREVIOUS view-state instead of going
              anywhere useful. Plus matter_ids was undefined on the
              detail payload (it's `matter_assignments` here, not
              `matter_ids`), so the fallback URL was always /workspace
              plain → user landed on inbox. The simpler model below
              picks the first matter from matter_assignments and is
              always coherent. Browser back-button stays available
              for "real" back. */}
            {(() => {
              // Back-button target priority:
              //   1. document.referrer (captured on mount) when same-
              //      origin and not another artifact viewer — handles
              //      Observability → Artifacts tab → click → back.
              //   2. /studio/artifacts when entered via Studio route
              //      with no usable referrer (cold open / new tab).
              //   3. /workspace?matter=<first>&artifact=<id> from the
              //      workspace flow, so back is always coherent.
              const firstMatterId = a.matter_assignments?.[0]?.matter ?? null
              const workspaceUrl = firstMatterId != null
                ? `/workspace?matter=${firstMatterId}&artifact=${a.id}`
                : `/workspace?artifact=${a.id}`
              const url = referrer
                ? referrer
                : (fromStudio ? '/studio/artifacts' : workspaceUrl)
              const titleText = referrer
                ? 'Back to where you came from'
                : (fromStudio ? 'Back to Studio Artifacts' : 'Open in workspace')
              return (
                <button
                  onClick={() => navigate(url)}
                  className="me-btn me-btn-outline"
                  title={titleText}
                >
                  <ArrowLeft size={12} />
                </button>
              )
            })()}
          <span className="me-chip uppercase">{a.artifact_type}</span>
          <span className="me-chip me-mono">{a.source_type}</span>
          <span className="me-mono text-[11px] me-text-mute" title={`internal id ${a.id}`}>{a.composite_id}</span>
          {a.parent_artifact && (
            <span className="me-chip me-mono flex items-center gap-1">
              <Paperclip size={10} /> attachment of #{a.parent_artifact}
            </span>
          )}
          {a.visibility === 'restricted' && (
            <span className="me-chip me-chip-stale flex items-center gap-1">
              <Lock size={10} /> restricted
            </span>
          )}
          {a.ignored && (
            <span
              className="inline-flex items-center gap-1 rounded-sm bg-[color:var(--me-stale)] px-2 py-0.5 text-[11px] font-medium text-white"
              title={`auto-ignored: ${a.ignored_reason}`}
            >
              <EyeOff size={11} /> IGNORED{a.ignored_reason ? ` · ${a.ignored_reason}` : ''}
            </span>
          )}
          <div className="ml-auto flex items-center gap-1">
            {/* Copy lives in the BodyPreview header on both pages now (parity
                with the workspace third pane). See onCopy handler below the
                Content section. */}
            {isAdmin && (
              <>
                <button
                  onClick={() => toggleIgnore.mutate()}
                  disabled={toggleIgnore.isPending}
                  className={a.ignored ? 'me-btn me-btn-primary' : 'me-btn me-btn-outline'}
                  title={a.ignored ? 'Restore — include in pipeline + browser default' : 'Hide from browser default + skip in pipeline runs'}
                >
                  {a.ignored ? <Eye size={11} /> : <EyeOff size={11} />}
                  {toggleIgnore.isPending ? '…' : (a.ignored ? 'Unignore' : 'Ignore')}
                </button>
                <button
                  onClick={() => run.mutate()}
                  disabled={run.isPending || a.ignored}
                  className="me-btn me-btn-primary"
                  title={a.ignored ? 'Unignore first to run' : ''}
                >
                  <Play size={11} />
                  {run.isPending ? 'Enqueuing…' : 'Run pipeline'}
                </button>
              </>
            )}
          </div>
        </div>

        {toggleIgnore.isSuccess && (
          <div className="mt-2 me-chip me-chip-fresh">
            {a.ignored ? '✓ Ignored — hidden from default browser, skipped in pipeline runs.' : '✓ Restored — back in default browser + eligible for pipeline runs.'}
          </div>
        )}

        {run.data && (
          <div className="mb-3 me-chip me-chip-fresh">enqueued · run #{run.data.run_id ?? 'pending'}</div>
        )}

        <h1 className="text-[22px] font-semibold leading-snug me-text">
          <EditableText
            value={a.subject || ''}
            target={{ type: 'artifact', id: a.id, field: 'subject' }}
            mode="inline"
            maxLength={998}
            invalidateKeys={[['artifact', a.id]]}
            placeholder="(no subject)"
          />
        </h1>

        <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px] me-text-dim">
          {a.from_email && (
            <>
              <span className="me-text-mute">From</span>
              <span>
                {a.from_name && <span className="font-medium">{a.from_name} </span>}
                <span className="me-mono">&lt;{a.from_email}&gt;</span>
              </span>
            </>
          )}
          {a.to_emails && a.to_emails.length > 0 && (
            <>
              <span className="me-text-mute">To</span>
              <span className="me-mono">{a.to_emails.join(', ')}</span>
            </>
          )}
          {a.cc_emails && a.cc_emails.length > 0 && (
            <>
              <span className="me-text-mute">Cc</span>
              <span className="me-mono">{a.cc_emails.join(', ')}</span>
            </>
          )}
          {a.source_date && (
            <>
              <span className="me-text-mute">Date</span>
              <span>{new Date(a.source_date).toLocaleString()}</span>
            </>
          )}
          {a.mime_type && (
            <>
              <span className="me-text-mute">Type</span>
              <span className="me-mono">{a.mime_type}</span>
            </>
          )}
        </div>

        <div className="mt-6 border-t me-border" />

        {/* Summary — AI-generated 2-4 sentence digest (canonical state
            written by the summarize_artifact step). Editable: user
            corrections persist + audit-trail to ManualEdit + the
            summarize_artifact prompt picks them up on next run. */}
        <section className="mt-5">
          <div className="me-section-h">Summary</div>
          <div className="me-card p-3 text-[12.5px] leading-snug me-text whitespace-pre-wrap">
            <EditableText
              value={a.ai_summary || ''}
              target={{ type: 'artifact', id: a.id, field: 'ai_summary' }}
              mode="block"
              multiline
              maxLength={4000}
              invalidateKeys={[['artifact', a.id]]}
              placeholder="(no AI summary yet)"
            />
          </div>
        </section>

        {/* Body */}
        <section className="mt-5">
          <BodyPreview
            mime={a.mime_type}
            cachedContent={a.cached_content}
            bodyHtml={a.body_html || ''}
            cachedFileUrl={a.cached_file_url}
            cachedFileSize={a.cached_file_size}
            storageMode={a.content_storage_mode}
            sourceType={a.source_type}
            sourceExternalId={a.source_external_id}
            onCopy={async () => {
              const ok = await copyArtifactToClipboard(a)
              if (ok) {
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
              }
            }}
            copied={copied}
          />
        </section>

        {/* Cross-mailbox dedup footer — list other mailbox copies of
            this email when this is the primary, or link back to the
            primary when this row is a duplicate. */}
        {a.duplicates?.length > 0 && (
          <section className="mt-3 me-card p-3 text-[12px] me-text-dim">
            <div className="me-mono uppercase tracking-wider me-text-mute mb-1 text-[10.5px]">
              Also received in
            </div>
            <ul className="space-y-0.5">
              {a.duplicates.map((d) => (
                <li key={d.id} className="me-mono">
                  {d.source_account_email || `(unknown mailbox) #${d.id}`}
                </li>
              ))}
            </ul>
          </section>
        )}
        {a.is_duplicate_of && (
          <section className="mt-3 me-card p-3 text-[12px] me-text-dim">
            This artifact is a cross-mailbox duplicate. The primary
            (with the AI analysis) is{' '}
            <a href={`${fromStudio ? '/studio' : ''}/artifact/${a.is_duplicate_of}`} className="underline">
              artifact #{a.is_duplicate_of}
            </a>
            .
          </section>
        )}

        {/* Comments */}
        {a.comments && a.comments.length > 0 && (
          <section className="mt-7">
            <div className="me-section-h flex items-center gap-1.5">
              <MessageSquare size={12} /> Comments · {a.comments.length}
            </div>
            <ul className="space-y-2">
              {a.comments.map((cAny, i) => {
                const c = cAny as Record<string, unknown>
                const author = (c.author_name as string) || (c.author_email as string) || 'unknown'
                const text = (c.text as string) || (c.content as string) || ''
                const quoted = (c.quoted as string) || (c.quoted_content as string) || ''
                const resolved = !!c.resolved
                const created = c.created_time as string | undefined
                const replies = (c.replies as Array<Record<string, unknown>> | undefined) ?? []
                return (
                  <li
                    key={i}
                    className={`me-card p-3 ${resolved ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="me-mono text-[11.5px] me-text-dim">{author}</span>
                      <div className="flex items-center gap-1.5">
                        {created && (
                          <span className="me-mono text-[10px] me-text-mute">
                            {new Date(created).toLocaleDateString()}
                          </span>
                        )}
                        {resolved && <span className="me-chip me-chip-fresh">resolved</span>}
                      </div>
                    </div>
                    {quoted && (
                      <blockquote className="me-mono mt-1 border-l-2 me-border pl-2 text-[11px] me-text-mute italic">
                        “{quoted.slice(0, 220)}{quoted.length > 220 ? '…' : ''}”
                      </blockquote>
                    )}
                    <div className="mt-1 text-[12.5px] me-text whitespace-pre-wrap">{text}</div>
                    {replies.length > 0 && (
                      <ul className="mt-2 space-y-1.5 border-l-2 me-border pl-3">
                        {replies.map((rAny, j) => {
                          const r = rAny as Record<string, unknown>
                          return (
                            <li key={j} className="text-[12px]">
                              <span className="me-mono me-text-dim">{(r.author_name as string) || (r.author_email as string)}</span>
                              {' — '}
                              <span className="me-text whitespace-pre-wrap">{(r.text as string) || (r.content as string) || ''}</span>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </li>
                )
              })}
            </ul>
          </section>
        )}

        {/* Redlines */}
        {a.redlines && a.redlines.length > 0 && (
          <section className="mt-7">
            <div className="me-section-h flex items-center gap-1.5">
              <Edit3 size={12} /> Redlines · {a.redlines.length}
            </div>
            <ul className="space-y-2">
              {a.redlines.map((rAny, i) => {
                const r = rAny as Record<string, unknown>
                const author = (r.author_name as string) || 'unknown'
                const action = ((r.action as string) || 'EDIT').toUpperCase()
                const before = (r.before as string) || (r.text as string) || ''
                const after = (r.after as string) || ''
                const ctx = (r.surrounding_context as string) || (r.paragraph_context as string) || ''
                const created = r.created_time as string | undefined
                const actionTone =
                  action.includes('DELETE')  ? 'me-chip-urgent' :
                  action.includes('INSERT')  ? 'me-chip-fresh'  :
                  action.includes('REPLACE') ? 'me-chip-stale'  :
                  ''
                return (
                  <li key={i} className="me-card p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="me-mono text-[11.5px] me-text-dim">{author}</span>
                      <div className="flex items-center gap-1.5">
                        {created && (
                          <span className="me-mono text-[10px] me-text-mute">
                            {new Date(created).toLocaleDateString()}
                          </span>
                        )}
                        <span className={`me-chip ${actionTone}`}>{action}</span>
                      </div>
                    </div>
                    {(before || after) && (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div>
                          <div className="me-mono text-[10px] me-text-mute uppercase tracking-wider">before</div>
                          <div className="me-mono mt-0.5 rounded-sm bg-[color:var(--me-urgentBg)] px-2 py-1 text-[12px] line-through text-[color:var(--me-urgent)]">
                            {before || '—'}
                          </div>
                        </div>
                        <div>
                          <div className="me-mono text-[10px] me-text-mute uppercase tracking-wider">after</div>
                          <div className="me-mono mt-0.5 rounded-sm bg-[color:var(--me-freshBg)] px-2 py-1 text-[12px] text-[color:var(--me-fresh)]">
                            {after || '—'}
                          </div>
                        </div>
                      </div>
                    )}
                    {ctx && (
                      <div className="mt-2 me-mono text-[10.5px] me-text-mute italic">
                        context: “{ctx.slice(0, 160)}{ctx.length > 160 ? '…' : ''}”
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </section>
        )}
      </article>

      {/* Sidebar — classifications + matters */}
      <aside className="me-scroll overflow-auto bg-[color:var(--me-bg)] p-4">
        <div className="me-section-h">Classifications</div>
        {results.isLoading && <div className="text-[12px] me-text-mute">loading…</div>}
        {!results.isLoading && (results.data ?? []).length === 0 && (
          <div className="text-[12px] me-text-mute italic">No pipeline runs yet.</div>
        )}
        <div className="space-y-1.5">
          {(results.data ?? []).map((r) => (
            <ClassificationCard key={r.id} r={r} />
          ))}
        </div>

        {a.matter_assignments && a.matter_assignments.length > 0 && (
          <>
            <div className="me-section-h mt-5">Matters</div>
            <div className="flex flex-wrap gap-1.5">
              {a.matter_assignments.map((am, i) => {
                const m = matterById.get(am.matter)
                return (
                  <span key={i} className="me-chip">
                    {m?.name ?? `#${am.matter}`}
                    {am.assigned_by === 'ai' && am.confidence != null && (
                      <span className="me-text-mute me-mono"> · {Math.round(am.confidence * 100)}%</span>
                    )}
                  </span>
                )
              })}
            </div>
          </>
        )}

        <div className="me-section-h mt-5">Source</div>
        <dl className="me-mono space-y-0.5 text-[11px]">
          <Field label="external_id" value={a.source_external_id} />
          <Field label="content_hash" value={a.content_hash?.slice(0, 16) + '…'} />
          <Field label="storage" value={a.content_storage_mode} />
          <Field label="status" value={a.source_status} />
          {a.last_seen_at && <Field label="last_seen" value={new Date(a.last_seen_at).toLocaleDateString()} />}
        </dl>
      </aside>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string | undefined }) {
  if (!value) return null
  return (
    <div className="grid grid-cols-[88px_1fr] gap-1">
      <dt className="me-text-mute">{label}</dt>
      <dd className="truncate me-text-dim">{value}</dd>
    </div>
  )
}

// formatArtifactForCopy + prettySize live in ../components/artifactCopy
// and ../components/BodyPreview respectively (download+toggle render is
// owned by BodyPreview now).

// BodyPreview is now shared with the workspace right pane; lives at
// src/components/BodyPreview.tsx.

// Make ArtifactDetail importable type for downstream consumers if they want.
export type { ArtifactDetail }
